import { useEffect, useState, useRef } from "react";
import { api, formatApiError, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Upload, Save, CheckCircle2, ImageIcon, PenLine } from "lucide-react";
import { toast } from "sonner";

function AssetUploader({ label, endpoint, hasAsset, previewKind, testId, onUploaded }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const currentSrc = hasAsset ? `${API}/company/${previewKind}?_=${reloadKey}` : null;
  const [remoteBlob, setRemoteBlob] = useState(null);

  useEffect(() => {
    let revoke;
    async function fetchAsset() {
      setRemoteBlob(null);
      if (!currentSrc) return;
      try {
        const res = await api.get(`/company/${previewKind}`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        revoke = url;
        setRemoteBlob(url);
      } catch (err) {
        if (err?.response?.status !== 404) {
          console.warn("CompanySettings.AssetUploader fetch failed", err);
        }
      }
    }
    fetchAsset();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [currentSrc, previewKind, reloadKey]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setPreview(null); return; }
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(f.type)) {
      toast.error("Format invalide — PNG, JPG ou WEBP requis");
      return;
    }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image trop volumineuse (max 5 Mo)"); return; }
    setPreview({ file: f, url: URL.createObjectURL(f) });
  };

  const upload = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", preview.file);
      await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} mis(e) à jour`);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      setReloadKey((k) => k + 1);
      onUploaded?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 border-slate-200" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        {previewKind === "logo" ? <ImageIcon className="h-4 w-4 text-[#0066FF]" /> : <PenLine className="h-4 w-4 text-[#0066FF]" />}
        <h3 className="font-heading font-semibold text-slate-900">{label}</h3>
      </div>

      <div className="rounded-lg border border-dashed border-slate-200 bg-[repeating-conic-gradient(#F1F5F9_0%,#F1F5F9_25%,#FFFFFF_0%,#FFFFFF_50%)] bg-[length:14px_14px] p-4 mb-4 min-h-[140px] flex items-center justify-center">
        {preview ? (
          <img src={preview.url} alt="preview" className="max-h-[130px] object-contain" />
        ) : remoteBlob ? (
          <img src={remoteBlob} alt="current" className="max-h-[130px] object-contain" />
        ) : (
          <div className="text-xs text-slate-400 text-center">
            <Upload className="h-6 w-6 mx-auto mb-1 opacity-60" />
            Aucun fichier — PNG transparent recommandé
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Input
          ref={fileRef}
          data-testid={`${testId}-file`}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={onFile}
          className="flex-1"
        />
        <Button
          data-testid={`${testId}-upload`}
          disabled={!preview || busy}
          onClick={upload}
          className="bg-[#0066FF] hover:bg-[#0052CC]"
        >
          {busy ? "…" : <><Upload className="h-4 w-4 mr-1" />Enregistrer</>}
        </Button>
      </div>
      {hasAsset && !preview && (
        <div className="mt-3 text-xs text-emerald-700 flex items-center gap-1">
          <CheckCircle2 className="h-3.5 w-3.5" /> Fichier actif · sera utilisé sur les prochains documents PDF
        </div>
      )}
    </Card>
  );
}

export default function RHCompanySettings() {
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", siret: "", signataire_nom: "", signataire_titre: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/company");
      setCompany(data);
      setForm({
        name: data.name || "",
        address: data.address || "",
        siret: data.siret || "",
        signataire_nom: data.signataire_nom || "",
        signataire_titre: data.signataire_titre || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/company", form);
      toast.success("Informations enregistrées");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader
        title="Paramètres Entreprise"
        subtitle="Configurez le logo, la signature du gérant et les informations qui apparaîtront sur les documents PDF (attestations de travail, attestations de congé)."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <AssetUploader
          label="Logo de l'entreprise"
          endpoint="/company/logo"
          previewKind="logo"
          hasAsset={!!company?.has_logo}
          testId="upload-logo"
          onUploaded={load}
        />
        <AssetUploader
          label="Signature du gérant"
          endpoint="/company/signature"
          previewKind="signature"
          hasAsset={!!company?.has_signature}
          testId="upload-signature"
          onUploaded={load}
        />
      </div>

      <Card className="mt-6 p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="h-4 w-4 text-[#0066FF]" />
          <h3 className="font-heading font-semibold text-slate-900">Informations légales de l'entreprise</h3>
        </div>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Raison sociale</Label>
            <Input data-testid="company-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="FIO TELECOM" />
          </div>
          <div>
            <Label>SIRET</Label>
            <Input data-testid="company-siret-input" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} placeholder="000 000 000 00000" />
          </div>
          <div className="md:col-span-2">
            <Label>Adresse</Label>
            <Textarea data-testid="company-address-input" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="12 avenue des Télécoms, 75000 Paris" />
          </div>
          <div>
            <Label>Nom du signataire</Label>
            <Input data-testid="company-signer-input" value={form.signataire_nom} onChange={(e) => setForm({ ...form, signataire_nom: e.target.value })} placeholder="Jean Dupont" />
          </div>
          <div>
            <Label>Titre du signataire</Label>
            <Input data-testid="company-signer-title-input" value={form.signataire_titre} onChange={(e) => setForm({ ...form, signataire_titre: e.target.value })} placeholder="Directeur RH" />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button data-testid="save-company-info" type="submit" disabled={saving} className="bg-[#0066FF] hover:bg-[#0052CC]">
              <Save className="h-4 w-4 mr-1" />{saving ? "…" : "Enregistrer"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
